import { FC, Fragment, useEffect, useMemo } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { adminMenuGroups } from '@service/admin/components/adminMenuConfig';
import {
  adminNavBackUrlAtom,
  adminNavBarUseAtom,
  adminNavBarUseNavLeftAtom,
  adminNavRightTextAtom,
  adminNavTitleAtom,
} from '@service/bugo/state';
import { Button } from '@shared/components/Button';
import { Icon } from '@shared/components/icons';
import { SidePopup } from '@shared/components/SidePopup';
import {
  releaseNoteChannelsOfRole,
  useReleaseNotes,
} from '@shared/releaseNotes/useReleaseNotes';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { WeakPasswordBadge } from '@shared/components/WeakPasswordBadge';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export const AdminNavBar: FC = function AdminNavBar() {
  const { Back, ChevronRight, EmptyUser, User, Menu, Home } = Icon();
  const openId = 'adminNavSidePopup';
  const setSideMenuOpen = useSetRecoilState(simpleOpenAtomFamily(openId));

  const location = useLocation();
  const pathName = location.pathname;
  const navigate = useNavigate();
  const title = useRecoilValue(adminNavTitleAtom);
  const rightText = useRecoilValue(adminNavRightTextAtom);
  const navBarUse = useRecoilValue(adminNavBarUseAtom);
  const navLeftUse = useRecoilValue(adminNavBarUseNavLeftAtom);
  const backUrl = useRecoilValue(adminNavBackUrlAtom);

  const { userProfile, apiLogout, isAuthenticated, roles } = useAuth();

  //* 사이드바는 홈 런처와 같은 정의를 쓰되, 사이드바 전용 제외 대상(그룹·항목)을 걷어낸다
  const sidebarMenuGroups = useMemo(
    () =>
      adminMenuGroups
        .filter((group) => !group.hideInSidebar)
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => !item.hideInSidebar),
        }))
        .filter((group) => group.items.length > 0),
    [],
  );

  //* 안 읽은 패치노트가 있으면 메뉴에 점을 찍는다 (읽음 여부는 기기별 localStorage)
  const releaseNoteChannels = useMemo(() => releaseNoteChannelsOfRole(roles), [roles]);
  const { hasUnread: hasUnreadReleaseNote } = useReleaseNotes(releaseNoteChannels);
  useEffect(() => {
    return setSideMenuOpen(false);
  }, [setSideMenuOpen]);

  const menuButton = (text: string, to: string) => {
    return (
      <Link
        to={to}
        className="flex min-h-[2rem] w-full items-center justify-between py-1 pl-1"
        onClick={() => {
          setSideMenuOpen(false);
        }}
      >
        <p className="bugo-h2">{text}</p>
        <ChevronRight />
      </Link>
    );
  };

  //* menuButton과 같은 모양에 안 읽음 점만 얹은 변형
  const menuButtonWithDot = (text: string, to: string, showDot: boolean) => {
    return (
      <Link
        to={to}
        className="flex min-h-[2rem] w-full items-center justify-between py-1 pl-1"
        onClick={() => {
          setSideMenuOpen(false);
        }}
      >
        <div className="flex items-center gap-1.5">
          <p className="bugo-h2">{text}</p>
          {showDot && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}
        </div>
        <ChevronRight />
      </Link>
    );
  };

  const bugoSideMenu = (login: boolean) => {
    //* 관리자 홈(AdminHomePage)과 같은 메뉴 정의를 공유 — adminMenuConfig 한 곳만 수정
    //* (hideInSidebar 그룹은 홈 런처에만 남기고 사이드바에서는 제외)
    const sharedMenu = (
      <>
        {sidebarMenuGroups.map((group) => (
          <div key={group.label}>
            <div className="px-4 pt-1.5 pb-0.5">
              <p className="text-xs font-bold leading-5 theme-text-8">{group.label}</p>
            </div>
            <div className="space-y-0.5 px-3 py-1 theme-bg-1">
              {group.items.map((item) => (
                <Fragment key={item.to}>
                  {item.releaseNoteDot
                    ? menuButtonWithDot(item.label, item.to, hasUnreadReleaseNote)
                    : menuButton(item.label, item.to)}
                </Fragment>
              ))}
            </div>
          </div>
        ))}
      </>
    );

    if (login) {
      return (
        <div className="relative min-h-screen w-64 theme-text-1 theme-bg-6">
          <div className="absolute right-0 top-0"></div>
          {/* 이름 블록은 내 계정(이름·연락처·비밀번호)으로 들어가는 링크 — 우측 > 가 실제로 동작한다 */}
          <Link
            to="/admin/settings"
            className="flex items-center justify-between pl-3 pr-2 pt-8 pb-3 theme-bg-1"
            onClick={() => {
              setSideMenuOpen(false);
            }}
          >
            <div className="flex items-center gap-2">
              <User />
              <p className="bugo-h1">{userProfile?.info?.name}님</p>
              <WeakPasswordBadge />
            </div>
            <ChevronRight />
          </Link>
          {/* <div>
              <div className="px-4 py-0.5">
                <p className="text-xs font-bold leading-5 theme-text-8">마이페이지</p>
              </div>
              <div className="space-y-2 px-3 py-2 theme-bg-1">
                {menuButton('적립금 관리', '/fevent/add')}
                {menuButton('나를 추천한 회원', '/fevent/manage')}
              </div>
            </div> */}
          {sharedMenu}
          <div className="flex items-center justify-end p-4">
            <Button
              className="button-fold text-xs leading-5 theme-text-8 theme-bg-1 theme-border-1"
              onClick={() => {
                apiLogout();
              }}
            >
              로그아웃
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="min-h-screen w-64 theme-text-1 theme-bg-6">
          <Link to="/login">
            <div className="flex items-center justify-between pl-3 pr-2 pt-8 pb-3 theme-bg-1">
              <div className="flex items-center gap-2">
                <EmptyUser />
                <p className="bugo-h1">로그인</p>
              </div>
              <ChevronRight />
            </div>
          </Link>
          {sharedMenu}
        </div>
      );
    }
  };

  const navLeft = () => {
    if (pathName === '/') {
      return (
        <Link to="/">
          관리자모드
          {/* <img src={} className="h-9"></img> */}
        </Link>
      );
    } else {
      return (
        <div className="flex items-center">
          {navLeftUse && (
            <button
              onClick={() => {
                backUrl ? navigate(backUrl) : navigate(-1);
              }}
              className="center-box wh-10"
            >
              <Back />
            </button>
          )}
          <p className="bugo-h1">{title}</p>
        </div>
      );
    }
  };

  const navRight = () => {
    if (rightText === '') {
      return (
        <div className="flex items-center">
          <button
            className="center-box wh-10"
            aria-label="부고 홈"
            onClick={() => navigate('/')}
          >
            <Home className="wh-5 theme-fill-1" />
          </button>
          <div
            className="center-box wh-10 cursor-pointer"
            onClick={() => setSideMenuOpen(true)}
          >
            <Menu />
          </div>
        </div>
      );
    } else {
      return (
        <div>
          <p className="bugo-h2 cursor-pointer theme-text-main">{rightText}</p>
        </div>
      );
    }
  };

  if (pathName.endsWith('login') || pathName.endsWith('error') || !navBarUse) {
    return null;
  } else {
    return (
      <>
        <div className="fixed z-20 flex h-14 w-full max-w-inherit items-center justify-between px-4 theme-bg-2">
          {navLeft()}
          {navRight()}
        </div>
        <SidePopup openId={openId}>{bugoSideMenu(isAuthenticated)}</SidePopup>
      </>
    );
  }
};
